
/**
 * LocalPresenceBroadcast — LocalPresence alt-sistemini TEBHOS gateway soketine
 * bağlayan köprü (kaynak-referansta aynı bağlama GatewayConnection içinde
 * `reaction(() => LocalPresence.presenceKey, ...) → socket.updatePresence(...)`
 * ile kurulur).
 *
 * TEK-DEPO GÖÇÜ (22 Eyl 2026) — BURADA NE DEĞİŞTİ:
 * Bu dosya eskiden `createTebhosLocalPresenceSettings()` ile KENDİ ayar yüzeyini
 * üretip `setLocalPresenceUserSettings` ile enjekte ediyordu; o yüzey durumu
 * eski `PresenceStore.myStatus` aynasından okuyordu. Ama `UserSettings` modülü
 * de açılışta aynı enjeksiyonu yapıyor → LocalPresence'ın ayar yüzeyi yükleme
 * sırasına göre değişiyordu ("tek alana iki yazar"): başka cihazdan gelen
 * USER_SETTINGS_UPDATE ayar deposunu tazeliyor, ama yayınlanan durum eski
 * localStorage aynasından geliyordu. Artık TEK sahip `UserSettings`tir; burası
 * yalnızca presenceKey → op3 yayınını ve açılış ipucunun tazelenmesini yapar.
 *
 * TEBHOS soket presence sözleşmesi (:9000) yalnızca `status` taşır (ws-client
 * `updatePresence(status)`); afk/mobile/custom_status wire'da YOK. :9100 (Erlang
 * v2) ise kaynak-referans 1-1 op3'ü tam parametre setiyle destekler
 * (GatewayV2Socket.updatePresence) — v2 aktifken bu köprünün gönderdiği
 * afk/mobile/customStatus GERÇEKTEN wire'a taşınır.
 */

import LocalPresence from '@app/features/presence/state/LocalPresence';
import {saveBootStatusHint} from '@app/features/presence/state/PresenceStore';
import type {GatewayCustomStatusPayload} from '@app/features/user/state/CustomStatus';
// SIRA GÜVENCESİ: ayar yüzeyini LocalPresence'a enjekte eden taraf UserSettings
// modülünün KENDİSİDİR (modül gövdesinde setLocalPresenceUserSettings). Köprü
// kurulmadan önce o modülün değerlendirilmiş olması gerekir; aksi halde ilk
// updatePresence() hidrasyonsuz çalışıp durumu "görünmez" sanır.
import '@app/features/user/state/UserSettings';
import {reaction} from 'mobx';

/** Köprünün ihtiyaç duyduğu minimal soket yüzeyi (ws-client.ts uyumlu). */
export interface LocalPresenceBroadcastSocket {
	updatePresence(
		status: string,
		afk?: boolean,
		mobile?: boolean,
		customStatus?: GatewayCustomStatusPayload | null,
	): void;
}

let installed = false;

/**
 * kaynak-referans-stili KENDİ presence yayınını etkinleştirir: `LocalPresence.presenceKey`
 * değiştikçe soket üzerinden tam presence gönderir.
 *
 * Ayar yüzeyi ENJEKTE EDİLMEZ — `UserSettings` modülü kendi yüklenmesinde
 * `setLocalPresenceUserSettings(UserSettings)` çağırır ve durumu/özel durumu
 * değiştiren her yol (`UserSettings.setStatus`, `updateUserSettings`) zaten
 * `LocalPresence.updatePresence()` tetikler. Burada ikinci bir kaynak yok.
 *
 * @returns Reaction'ı sökmek için disposer.
 */
export function installLocalPresenceBroadcast(socket: LocalPresenceBroadcastSocket): () => void {
	if (installed) {
		// Çift kurulumu engelle — ilk kurulum kazanır.
		return () => {};
	}
	installed = true;
	LocalPresence.updatePresence();

	const dispose = reaction(
		() => LocalPresence.presenceKey,
		() => {
			const presence = LocalPresence.getGatewayPresence();
			if (!presence) return;
			// Bir sonraki açılışta IDENTIFY(op2) doğru durumla gitsin (READY'den
			// önce sunucudan gelmiş bir durum yoktur).
			saveBootStatusHint(presence.status);
			try {
				// kaynak-referans 1-1: tam presence gönder (status + afk + mobile +
				// custom_status). :9000'te ws-client bunları düşürür (yalnız status
				// gider); :9100'te GERÇEKTEN wire'a taşınır.
				socket.updatePresence(presence.status, presence.afk, presence.mobile, presence.custom_status);
			} catch {
				// Soket geçici olarak yoksa/bağlı değilse sessizce geç — bir sonraki
				// presenceKey değişiminde tekrar denenir.
			}
		},
		{fireImmediately: true},
	);

	return () => {
		dispose();
		installed = false;
	};
}
